import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertService, AlertType } from 'src/app/shared/services/alert.service';
import { MedicalRecordLookupService } from 'src/app/shared/services/API/medical-record/lookup.service';
import { PrescriptionSchedulingService } from 'src/app/shared/services/API/orchestrator-patient/prescription-scheduling.service';
import { VerifyPermissionService } from 'src/app/shared/services/API/user/verify-permission.service';
import { VerifyPermissionRequest } from 'src/app/shared/services/requests/user/verify-permission.request';
import { UtilService } from 'src/app/shared/services/util.service';
import { CheckRestrictedOverdueMedicationModalComponent } from '../../../check-restricted-overdue-medication-modal/check-restricted-overdue-medication-modal.component';
import { UserRoleEnum } from 'src/app/shared/enum/user-role.enum';
import { VerifyPermissionResponse } from 'src/app/shared/services/responses/user/verify-permission.response';
import { StatusSchedulingEnum } from 'src/app/shared/enum/medical-record/status-scheduling.enum';
import { MedicalProcedureStruct } from 'src/app/shared/services/structs/medical-record/medical-procedure.struct';
import { MedicalProcedureCheckRequest } from 'src/app/shared/services/requests/medical-record/medical-procedure-check.request';
import { MedicalProcedureCheckStruct } from 'src/app/shared/services/structs/orchestrator-patient/medical-procedure-check.struct';
import { MedicalProcedureModalComponent } from 'src/app/observation/pages/bed-management-details/procedure-list/medical-procedure-modal/medical-procedure-modal.component';

@Component({
    selector: 'app-late-pending-procedure-checks',
    templateUrl: './late-pending-procedure-checks.component.html',
    styleUrls: ['./late-pending-procedure-checks.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LatePendingProcedureChecksComponent implements OnInit {
  @Input() medicalProcedureStruct: MedicalProcedureStruct;
  @Input() patientName: string;
  @Input() birthDate: Date;
  @Input() idEpisode: number;
  @Input() listCheckedProcedure: MedicalProcedureCheckRequest[] = [];

  public statusSchedulingEnum: typeof StatusSchedulingEnum = StatusSchedulingEnum;

  constructor(
    public dialog: MatDialog,
    private alertService: AlertService,
    public prescriptionSchedulingService: PrescriptionSchedulingService,
    public medicalRecordLookupService: MedicalRecordLookupService,
    public verifyPermissionService: VerifyPermissionService,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {}

  async onClickInProcedureCheck(medicalProcedureCheck: MedicalProcedureCheckStruct): Promise<void> {
    let verifyPermissionResponse: VerifyPermissionResponse = null;

    let isToVerifyPermission = (medicalProcedureCheck.idStatusScheduling !== StatusSchedulingEnum.Suspenso 
                             && medicalProcedureCheck.idStatusScheduling !== StatusSchedulingEnum.Concluido);

    if (isToVerifyPermission) {
      verifyPermissionResponse = await this.checkRestrictedOverdueMedication();
      if (!verifyPermissionResponse?.havePermission) 
        return;
    }    

    this.openCheckProcedureModal(medicalProcedureCheck, verifyPermissionResponse);
  }

  async checkRestrictedOverdueMedication(): Promise<VerifyPermissionResponse> {
    return new Promise((resolve) => {
      if (!this.hasRoleToCheckOverduePrescription()) {
        this.alertService.show('Erro', 'O usuário não possui permissão para liberar a checagem com restrição.', AlertType.error);
        resolve(null);
        return;
      }

      const dialogRef = this.dialog.open(CheckRestrictedOverdueMedicationModalComponent);

      dialogRef.afterClosed().subscribe(async (result) => {
        const hasPopulatedResult = result?.confirm && result?.response;

        if (!hasPopulatedResult) {
          resolve(null);
          return;
        }

        resolve(result?.response);
      });
    });
  }

  hasRoleToCheckOverduePrescription(): boolean {
    const listRole = this.utilService.getRoles();
    const isMaster = this.utilService.getToken().isMaster;

    return (listRole.some((x) => x.idRole === UserRoleEnum.checar_atrasado_com_restrição) || isMaster);
  }

  openCheckProcedureModal(medicalProcedureCheck: MedicalProcedureCheckStruct, verifyPermissionResponse: VerifyPermissionResponse) {
    const dialogRef = this.dialog.open(MedicalProcedureModalComponent, {
      data: {
        procedure: this.medicalProcedureStruct,
        patientName: this.patientName,
        birthDate: this.birthDate,
        idMedicalProcedure: this.medicalProcedureStruct.idMedicalProcedure,
        actions: true,
        idEpisode: this.idEpisode,
        selectedMedicalProcedureCheck: medicalProcedureCheck,
        useScheduling: true,
        verifyPermissionResponse: verifyPermissionResponse
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.medicalProcedureCheckRequest && result?.medicalProcedureCheckRequest instanceof MedicalProcedureCheckRequest) {
        this.updateCheckedProcedure(result.medicalProcedureCheckRequest);
        this.updateProcedureCheck(result.medicalProcedureCheckRequest, medicalProcedureCheck);
      }
    });
  }

  updateCheckedProcedure(medicalProcedureCheckRequest: MedicalProcedureCheckRequest): void {
    const index = this.listCheckedProcedure.findIndex(l => l.idMedicalProcedure == medicalProcedureCheckRequest.idMedicalProcedure);
    if (index >= 0)
      this.listCheckedProcedure[index] = medicalProcedureCheckRequest
    else
      this.listCheckedProcedure.push(medicalProcedureCheckRequest);    
  }

  updateProcedureCheck(medicalProcedureCheckRequest: MedicalProcedureCheckRequest, medicalProcedureCheck: MedicalProcedureCheckStruct): void {
    const indexProcedureCheck = this.medicalProcedureStruct.listMedicalProcedureCheck.findIndex(x => x.idMedicalProcedureCheck === medicalProcedureCheckRequest.idMedicalProcedureCheck);

    if (indexProcedureCheck >= 0) {
      medicalProcedureCheck.idStatusScheduling = medicalProcedureCheckRequest.idStatusScheduling;
      medicalProcedureCheck.reasonSuspension = medicalProcedureCheckRequest.reasonSuspension;
      this.medicalProcedureStruct.listMedicalProcedureCheck[indexProcedureCheck] = medicalProcedureCheck;
    }
  }
}
